import type { EventBus } from "../core/EventBus.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Projectile } from "../entities/Projectile.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import type { SynergySystem } from "./SynergySystem.ts";
import type { ObjectPool } from "../utils/ObjectPool.ts";
import type { Firewall } from "../entities/Firewall.ts";
import type { CellBlockedPredicate } from "../core/LineOfSight.ts";
import { updateStealthReveal } from "./BeamCombat.ts";
import { applyFirewallSlow, updateFirewallDamage } from "./FirewallCombat.ts";
import { COMBAT } from "../core/config.ts";
import { updateProjectiles, checkProjectileHits } from "./ProjectileCombat.ts";
import { updateContagionSpread } from "./ContagionSpread.ts";
import { updateHazardPools } from "./HazardPool.ts";
import type { HazardPoolZone } from "./HazardPool.ts";
import { TOWER_BEHAVIORS } from "./towerBehaviors.ts";
import type { TowerUpdateContext } from "./towerBehaviors.ts";
import { applyCommandAura } from "./GoldAura.ts";
import type { ApexMod } from "./DamageCalculation.ts";

export interface CombatPerfFrame {
  contagionMs: number;
  clusterMs: number;
  virusTowerUpdates: number;
}

interface ProjectileContext {
  projectilePool: ObjectPool<Projectile>;
  draftSystem: DraftSystem | null;
  synergySystem: SynergySystem | null;
  elementBonuses: Record<string, number>;
  towers: Tower[];
  enemies: Enemy[];
  bus: EventBus;
  bossDamageMult: number;
  globalDamageMult: number;
  apex: ApexMod | null;
  piercingBonus: number;
  hazardPools: HazardPoolZone[];
  rng: () => number;
}

interface BeamCombatContext {
  enemies: Enemy[];
  towers: Tower[];
  rangeMultiplier: number;
  draftSystem: DraftSystem | null;
  elementBonuses: Record<string, number>;
  globalDamageMult: number;
  apex: ApexMod | null;
  bossDamageMult: number;
  preferMarked: boolean;
  aoeMark: boolean;
  aoeMarkRadius: number;
  losBlockedAt: CellBlockedPredicate | null;
  bus: EventBus;
  rng: () => number;
}

/** Orchestrates combat: tower firing, projectile movement, damage application. */
export class CombatSystem {
  private towers: Tower[];
  private enemies_: Enemy[];
  private unsubscribers: Array<() => void> = [];
  private projectilePool: ObjectPool<Projectile>;
  private bus: EventBus;
  private draftSystem: DraftSystem | null = null;
  private synergySystem: SynergySystem | null = null;
  private timeWarpActive_ = false;
  private timeWarpTimer = 0;
  private piercingBonus_ = 0;
  private globalSpeedMultiplier_ = 1;
  private elementBonuses: Record<string, number> = {};
  private rangeMultiplier_ = 1;
  private bossDamageReduction_ = 1;
  private bossWaveCounter = 0;
  private globalDamageMult_ = 1;
  // BL-72: per-frame dynamic card damage stack (MINIMALIST × RESONANCE ×
  // DEAD_MAN'S_SWITCH × GREED penalty) folded into the contexts' globalDamageMult,
  // and the per-element APEX_PREDATOR modifier. Both neutral (1 / null) when no
  // such card is active, so the deterministic baseline is byte-unchanged.
  private dynCardMult_ = 1;
  private apexMod_: ApexMod | null = null;
  private aoeMark_ = false;
  private aoeMarkRadius_ = 48;
  private preferMarked_ = false;
  private targetFastest_ = false;
  private firewalls_: Firewall[] = [];
  private losBlockedAt_: CellBlockedPredicate | null = null;
  private firewallSlowActive_ = false;
  private firewallSlowFactor_ = 1;
  private readonly FIREWALL_SLOW_RANGE = COMBAT.FIREWALL_SLOW_RANGE;
  private hazardPools_: HazardPoolZone[] = [];
  private rng: () => number;
  private simTimeSec_ = 0;
  private perfEnabled_ = false;
  private perfFrame_: CombatPerfFrame = {
    contagionMs: 0,
    clusterMs: 0,
    virusTowerUpdates: 0,
  };
  private projCtx_!: ProjectileContext;
  private beamCtx_!: BeamCombatContext;

  constructor(
    towers: Tower[],
    enemies: Enemy[],
    projectilePool: ObjectPool<Projectile>,
    bus: EventBus,
    rng: () => number,
  ) {
    this.towers = towers;
    this.enemies_ = enemies;
    this.projectilePool = projectilePool;
    this.bus = bus;
    this.rng = rng;
    this.buildContextCaches();
    // New enemies must inherit the current global-speed multiplier so that
    // spawns mid-card don't ignore the active modifier.
    this.unsubscribers.push(
      this.bus.on("enemy.spawned", (data: unknown) => {
        const ev = data as { enemy: Enemy };
        this.applyGlobalSpeedTo(ev.enemy);
      }),
    );
    // B7 — a freeze belongs to the wave it was cast in. Clearing it here (and
    // not, say, on `wave.completed`) is deliberate: a wave can complete
    // mid-freeze, and the freeze should still be visibly running out during
    // the between-waves moment. What must never happen is a wave STARTING
    // frozen, because its spawn queue cannot advance while the warp is up.
    this.unsubscribers.push(
      this.bus.on("wave.started", () => this.clearTimeWarp()),
    );
  }

  /** Re-baseline enemy baseSpeed from originalBaseSpeed (idempotent). */
  private applyGlobalSpeedTo(enemy: Enemy): void {
    enemy.baseSpeed = Math.round(
      enemy.originalBaseSpeed * this.globalSpeedMultiplier_,
    );
  }

  set enemies(list: Enemy[]) {
    this.enemies_ = list;
    this.projCtx_.enemies = list;
    this.beamCtx_.enemies = list;
  }
  get timeWarpActive(): boolean {
    return this.timeWarpActive_;
  }

  setDraftSystem(ds: DraftSystem): void {
    this.draftSystem = ds;
    this.projCtx_.draftSystem = ds;
    this.beamCtx_.draftSystem = ds;
  }
  setSynergySystem(ss: SynergySystem): void {
    this.synergySystem = ss;
    this.projCtx_.synergySystem = ss;
  }

  set globalSpeedMultiplier(val: number) {
    this.globalSpeedMultiplier_ = val;
    for (const enemy of this.enemies_) this.applyGlobalSpeedTo(enemy);
  }

  get globalSpeedMult(): number {
    return this.globalSpeedMultiplier_;
  }

  applyElementBonus(element: string, multiplier: number): void {
    this.elementBonuses[element] =
      (this.elementBonuses[element] ?? 1) * multiplier;
  }

  /** Read-only view of the composed per-element damage bonuses — the SAME
   *  object `projCtx_`/`beamCtx_` already carry into `calculateDamage`. It
   *  exists so the effect is observable from outside without reaching into the
   *  private field: the operator damage-domain test feeds it to the real
   *  `calculateDamage`, which is what makes that test a runtime-effect check
   *  rather than one more assertion about a system property. */
  get elementBonusView(): Readonly<Record<string, number>> {
    return this.elementBonuses;
  }

  /** Compose a run-start range contribution multiplicatively (F-03).
   *  Single home for the stacking rule — operator + module contributions
   *  must never overwrite each other regardless of application order. */
  applyRangeMultiplier(multiplier: number): void {
    this.rangeMultiplier_ *= multiplier;
  }

  /**
   * Activate the time-warp freeze. `durationMs` is MILLISECONDS, matching
   * `timeWarpTimer`, which `update` decrements by `dt` (also milliseconds).
   *
   * GA-40 — the unit is deliberately millisecond here, NOT seconds. The defect
   * was on the CALLER side: `gs.timeWarpDuration` is documented and initialised
   * in SECONDS (`stateIntents.setTimeWarpDuration`, `main.ts`, `resetForNewRun`
   * → 5) and was passed through raw, so the timer got 5 ms — under a third of
   * one 60 FPS frame. The ability burned its charge and played its SFX while
   * freezing nothing. The two production call sites now convert; the seconds →
   * milliseconds boundary lives with them, and this method keeps the unit its
   * own field uses. (Converting here instead would have silently multiplied the
   * already-correct millisecond callers — `simHarness.activateTimeWarp` and the
   * baseline golden-master scenario — by 1000, stalling their waves.)
   *
   * Pairs with GA-41: now that the freeze lasts its full duration, the
   * wave-completion check must keep running during it (see `SimStep`).
   */
  activateTimeWarp(durationMs: number): void {
    // B7 (spec `2026-time-warp-softlock`) — a freeze may never be EXTENDED by
    // re-triggering it. Re-arming used to reset the timer, so a warp
    // re-triggered faster than it expires never expires; and because
    // `SimStep`'s freeze branch does not run `waveManager.update`, the wave's
    // completion condition (`spawnIndex >= spawnQueue.length`) can never
    // become true while it is active. The two together are the reported
    // softlock exactly: enemies frozen, still killable, wave never ending.
    //
    // Ignoring re-activation (rather than clamping the total) keeps the
    // authored duration honest and keeps GA-40 closed — the freeze still lasts
    // its full 5 s, it simply cannot be stacked into an unbounded one.
    if (this.timeWarpActive_) return;
    this.timeWarpActive_ = true;
    this.timeWarpTimer = durationMs;
  }

  /** B7 — end any active freeze. A freeze belongs to the wave it was cast in.
   *
   *  A wave CAN complete mid-freeze (the queue is exhausted and the last enemy
   *  dies while frozen — precisely what GA-41 enabled), which left the warp
   *  still running as the next wave began. That next wave then started frozen,
   *  with its spawn queue unable to advance: a wave frozen from its very first
   *  frame, which is the same dead end from the other direction. */
  clearTimeWarp(): void {
    this.timeWarpActive_ = false;
    this.timeWarpTimer = 0;
  }

  set rangeMultiplier(val: number) {
    this.rangeMultiplier_ = val;
  }

  get rangeMultiplier(): number {
    return this.rangeMultiplier_;
  }

  set globalDamageMult(val: number) {
    this.globalDamageMult_ = val;
  }

  get globalDamageMult(): number {
    return this.globalDamageMult_;
  }

  set bossDamageReduction(val: number) {
    this.bossDamageReduction_ = val;
    this.bossWaveCounter = 5;
  }

  get bossDamageReduction(): number {
    return this.bossDamageReduction_;
  }

  get bossDamageMult(): number {
    return this.bossWaveCounter > 0 ? this.bossDamageReduction_ : 1;
  }

  decrementBossWaveCounter(): void {
    if (this.bossWaveCounter > 0) this.bossWaveCounter--;
  }

  set piercingBonus(val: number) {
    this.piercingBonus_ = val;
  }

  get piercingBonus(): number {
    return this.piercingBonus_;
  }

  set aoeMark(val: boolean) {
    this.aoeMark_ = val;
  }
  set aoeMarkRadius(val: number) {
    this.aoeMarkRadius_ = val;
  }
  set preferMarked(val: boolean) {
    this.preferMarked_ = val;
  }
  get preferMarked(): boolean {
    return this.preferMarked_;
  }
  set targetFastest(val: boolean) {
    this.targetFastest_ = val;
  }
  get targetFastest(): boolean {
    return this.targetFastest_;
  }

  set firewalls(list: Firewall[]) {
    this.firewalls_ = list;
  }
  set firewallSlowActive(val: boolean) {
    this.firewallSlowActive_ = val;
  }
  set firewallSlowFactor(val: number) {
    this.firewallSlowFactor_ = val;
  }

  set losBlocked(fn: CellBlockedPredicate | null) {
    this.losBlockedAt_ = fn;
    this.beamCtx_.losBlockedAt = fn;
  }

  private buildContextCaches(): void {
    this.projCtx_ = {
      projectilePool: this.projectilePool,
      draftSystem: this.draftSystem,
      synergySystem: this.synergySystem,
      elementBonuses: this.elementBonuses,
      towers: this.towers,
      enemies: this.enemies_,
      bus: this.bus,
      bossDamageMult: this.bossDamageMult,
      globalDamageMult: this.globalDamageMult_,
      apex: null,
      piercingBonus: this.piercingBonus_,
      hazardPools: this.hazardPools_,
      rng: this.rng,
    };
    this.beamCtx_ = {
      enemies: this.enemies_,
      towers: this.towers,
      rangeMultiplier: this.rangeMultiplier_,
      draftSystem: this.draftSystem,
      elementBonuses: this.elementBonuses,
      globalDamageMult: this.globalDamageMult_,
      apex: null,
      bossDamageMult: this.bossDamageMult,
      preferMarked: this.preferMarked_,
      aoeMark: this.aoeMark_,
      aoeMarkRadius: this.aoeMarkRadius_,
      losBlockedAt: this.losBlockedAt_,
      bus: this.bus,
      rng: this.rng,
    };
  }

  destroy(): void {
    for (const off of this.unsubscribers) off();
    this.unsubscribers.length = 0;
  }

  reset(): void {
    this.timeWarpActive_ = false;
    this.timeWarpTimer = 0;
    this.piercingBonus_ = 0;
    this.globalSpeedMultiplier_ = 1;
    this.elementBonuses = {};
    this.rangeMultiplier_ = 1;
    this.bossDamageReduction_ = 1;
    this.bossWaveCounter = 0;
    this.globalDamageMult_ = 1;
    this.dynCardMult_ = 1;
    this.apexMod_ = null;
    this.aoeMark_ = false;
    this.aoeMarkRadius_ = 48;
    this.preferMarked_ = false;
    this.targetFastest_ = false;
    this.losBlockedAt_ = null;
    this.firewallSlowActive_ = false;
    this.firewallSlowFactor_ = 1;
    this.hazardPools_.length = 0;
    this.simTimeSec_ = 0;
    this.buildContextCaches();
  }

  getSimTime(): number {
    return this.simTimeSec_;
  }

  setPerfEnabled(enabled: boolean): void {
    this.perfEnabled_ = enabled;
  }

  consumePerfFrame(): CombatPerfFrame {
    return {
      contagionMs: this.perfFrame_.contagionMs,
      clusterMs: this.perfFrame_.clusterMs,
      virusTowerUpdates: this.perfFrame_.virusTowerUpdates,
    };
  }

  update(dt: number, currentLives = Infinity): void {
    this.perfFrame_.contagionMs = 0;
    this.perfFrame_.clusterMs = 0;
    this.perfFrame_.virusTowerUpdates = 0;

    this.simTimeSec_ += dt / 1000;
    const virusMods = this.synergySystem?.getVirusSynergyMods() ?? null;

    if (this.timeWarpActive_) {
      this.timeWarpTimer -= dt;
      if (this.timeWarpTimer <= 0) this.timeWarpActive_ = false;
    }

    // Sync synergy-driven flags
    if (this.synergySystem) {
      this.preferMarked_ = this.synergySystem.hasAutoTargetMarked();
      const aoeR = this.synergySystem.getAoeMarkRadius();
      this.aoeMark_ = aoeR > 0;
      if (aoeR > 0) this.aoeMarkRadius_ = aoeR;
    }

    // BL-72: recompute the dynamic card-damage stack + APEX modifier once per
    // frame so the contexts (and thus every damage path) read them this tick.
    this.refreshCardModifiers(currentLives);

    const beamCtx = this.getBeamCombatContext();
    updateStealthReveal(beamCtx, this.simTimeSec_);
    this.updateTowers(dt, beamCtx);
    updateProjectiles(this.projectilePool, dt);
    checkProjectileHits(this.getProjectileContext());
    if (this.perfEnabled_) {
      const t0 = performance.now();
      updateContagionSpread(this.enemies_, dt, {
        rng: this.rng,
        bus: this.bus,
        virusMods,
      });
      this.perfFrame_.contagionMs += performance.now() - t0;
    } else {
      updateContagionSpread(this.enemies_, dt, {
        rng: this.rng,
        bus: this.bus,
        virusMods,
      });
    }
    updateHazardPools(this.hazardPools_, this.enemies_, dt, {
      bus: this.bus,
      virusMods,
    });
    const fwCtx = {
      firewalls: this.firewalls_,
      enemies: this.enemies_,
      firewallSlowActive: this.firewallSlowActive_,
      firewallSlowFactor: this.firewallSlowFactor_,
      firewallSlowRange: this.FIREWALL_SLOW_RANGE,
      bus: this.bus,
    };
    applyFirewallSlow(fwCtx);
    updateFirewallDamage(dt, fwCtx);
  }

  private getProjectileContext() {
    const ctx = this.projCtx_;
    ctx.bossDamageMult = this.bossDamageMult;
    ctx.globalDamageMult = this.globalDamageMult_ * this.dynCardMult_;
    ctx.apex = this.apexMod_;
    ctx.piercingBonus = this.piercingBonus_;
    return ctx;
  }

  getHazardPools(): readonly HazardPoolZone[] {
    return this.hazardPools_;
  }

  private getBeamCombatContext() {
    const ctx = this.beamCtx_;
    ctx.rangeMultiplier = this.rangeMultiplier_;
    ctx.globalDamageMult = this.globalDamageMult_ * this.dynCardMult_;
    ctx.apex = this.apexMod_;
    ctx.bossDamageMult = this.bossDamageMult;
    ctx.preferMarked = this.preferMarked_;
    ctx.aoeMark = this.aoeMark_;
    ctx.aoeMarkRadius = this.aoeMarkRadius_;
    ctx.losBlockedAt = this.losBlockedAt_;
    return ctx;
  }

  /** BL-72: recompute the per-frame dynamic card-damage stack + APEX modifier.
   *  Every factor is neutral (1 / null) when its card isn't active, so this is a
   *  no-op for the deterministic baseline (which never drafts). */
  private refreshCardModifiers(currentLives: number): void {
    const ds = this.draftSystem;
    if (!ds) {
      this.dynCardMult_ = 1;
      this.apexMod_ = null;
      return;
    }
    const synergyCount = this.synergySystem?.activeSynergies.length ?? 0;
    this.dynCardMult_ =
      ds.getMinimalistBonus() *
      ds.getResonanceBonus(synergyCount) *
      ds.getDeadMansSwitchMultiplier(currentLives) *
      ds.getTradeoffDamagePenalty();
    const apex = ds.getApexPredatorEffect();
    this.apexMod_ = apex
      ? {
          dominant: this.dominantElement(),
          boost: apex.boost,
          penalty: apex.penalty,
        }
      : null;
  }

  /** Most-represented tower element. Deterministic: max count, lexicographic
   *  tie-break (independent of tower insertion order). */
  private dominantElement(): string {
    const counts = new Map<string, number>();
    for (const t of this.towers) {
      counts.set(t.element, (counts.get(t.element) ?? 0) + 1);
    }
    let best = "";
    let bestCount = -1;
    for (const [el, c] of counts) {
      if (c > bestCount || (c === bestCount && el < best)) {
        bestCount = c;
        best = el;
      }
    }
    return best;
  }

  private updateTowers(dt: number, beamCtx: BeamCombatContext): void {
    const ctx: TowerUpdateContext = {
      beam: beamCtx,
      projectile: this.getProjectileContext(),
      enemies: this.enemies_,
      rangeMultiplier: this.rangeMultiplier_,
      targetFastest: this.targetFastest_,
      draftSystem: this.draftSystem,
      losBlockedAt: this.losBlockedAt_,
      activeSynergies: this.synergySystem?.activeSynergies ?? [],
    };
    // BL-175 / D-266 — recompute signal's GOLD "ORACLE · Command Aura" BEFORE the
    // behaviors fire, so a buffed tower's shots this frame already carry the buff.
    // Inert with no GOLD signal tower (every `commandAuraMult` stays 1) → no
    // surface in any fixture, byte-identical.
    applyCommandAura(this.towers, this.rangeMultiplier_, this.enemies_);
    for (const tower of this.towers) {
      // BL-152 (S154) — the Jammer lever. A tower stamped by a living Act-II
      // Jammer (jammedTimer > 0) runs on "slow-time": its per-frame dt is scaled
      // by the STAMPED `jammedFireRateMult` (S156/PT-41: lifted out of the
      // `JAM.FIRE_RATE_MULT` constant into `enemies.json` `special.fireRateMult`,
      // stamped beside the timer), throttling fire-rate / beam-DPS / sniper-charge
      // uniformly (one place, every tower type). The stamp decays by RAW dt so a
      // killed/departed Jammer un-jams the tower in ~WINDOW_MS/dt frames. GUARDED:
      // an un-jammed tower (all of Act I, every tower out of range) keeps
      // `towerDt === dt` → the behavior call is byte-identical to pre-S154.
      let towerDt = dt;
      if (tower.jammedTimer > 0) {
        tower.jammedTimer = Math.max(0, tower.jammedTimer - dt);
        towerDt = dt * tower.jammedFireRateMult;
      }
      if (this.perfEnabled_ && tower.element === "virus") {
        const t0 = performance.now();
        TOWER_BEHAVIORS[tower.element](tower, towerDt, ctx);
        this.perfFrame_.clusterMs += performance.now() - t0;
        this.perfFrame_.virusTowerUpdates++;
        continue;
      }
      TOWER_BEHAVIORS[tower.element](tower, towerDt, ctx);
    }
  }
}
